/*
    Block components are dynamically imported so each becomes its own chunk.
    At publish time, <link rel="modulepreload"> tags are injected for only the
    chunks a funnel actually needs. Before the first render, preloadBlocks()
    resolves the imports from the browser's preload cache into a synchronous Map,
    so getBlockComponent() stays synchronous and Block.svelte needs no changes.

    See: https://www.notion.so/perspectiveco/Reduce-Funnel-bundle-size-2abf87b6a84480caa9bccad11bb2c50d
*/

import type { ComponentType } from 'svelte';

const importMap: Record<string, () => Promise<{ default: ComponentType }>> = {
    accordion: () => import('./Accordion'),
    button: () => import('./Button'),
    countdown: () => import('./Countdown'),
    divider: () => import('./Divider'),
    embed: () => import('./Embed'),
    gridColumn: () => import('./GridColumn'),
    gridRow: () => import('./GridRow'),
    headerButton: () => import('./HeaderButton'),
    headerImage: () => import('./HeaderImage'),
    icon: () => import('./Icon'),
    image: () => import('./Image'),
    list: () => import('./List'),
    loader: () => import('./Loader'),
    logos: () => import('./Logos'),
    paymentForm: () => import('./PaymentForm'),
    questionForm: () => import('./FormQuestion'),
    questionMedia: () => import('./MediaQuestion'),
    questionMultipleChoice: () => import('./MultipleChoiceQuestion'),
    questionText: () => import('./TextQuestion'),
    reviews: () => import('./Reviews'),
    sliderImage: () => import('./ImageSlider'),
    sliderTestimonial: () => import('./TestimonialSlider'),
    text: () => import('./Text'),
    video: () => import('./Video'),
    videoQuestion: () => import('./VideoQuestion'),
    webinar: () => import('./Webinar'),
};

const loadedBlocks = new Map<string, ComponentType>();

/**
 * Pre-load block components into the synchronous cache.
 * When <link rel="modulepreload"> tags are present in the HTML,
 * the dynamic import() calls resolve from the browser's preload cache
 * almost instantly (no network request).
 *
 * Must be called and awaited before the first page render.
 */
export const preloadBlocks = async (blockTypes: string[]): Promise<void> => {
    await Promise.all(
        blockTypes.map(async (type) => {
            const importer = importMap[type];

            if (importer && !loadedBlocks.has(type)) {
                const mod = await importer();
                loadedBlocks.set(type, mod.default);
            }
        }),
    );
};

/**
 * Synchronous component lookup — identical API to the original.
 * Returns undefined if the block type was not pre-loaded.
 */
export const getBlockComponent = (componentType: string, content: any) => {
    if (componentType === 'media') {
        if (content?.srcType === 'image') {
            return loadedBlocks.get('image');
        } else if (content?.srcType === 'video') {
            return loadedBlocks.get('video');
        } else {
            console.error(`Unknown media srcType: ${content?.srcType}`);

            return undefined;
        }
    }

    const component = loadedBlocks.get(componentType);

    if (!component) {
        console.error(`Unknown component type: ${componentType}`);
    }

    return component;
};

export const ALL_BLOCK_TYPES = Object.keys(importMap);
